import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import { Container } from "reactstrap";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";

/* -------------------------------------------------------------------------
 * AdminMarketInsightPage — full "Customer Insights" table view. No sidebar or
 * header here; this is content-only, dropped into whatever shell layout
 * the app already provides.
 * ---------------------------------------------------------------------- */

const AdminMarketInsightPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Market Insights | Omaha Food Lovers";
  const columns = [
    { key: "merchantName", label: "Merchant", width: "1.5fr" },
    { key: "customerName", label: "Customer", width: "1.2fr" },
    { key: "totalSpend", label: "Total Spend", width: "1fr", render: (r) => `$${r.totalSpend.toFixed(2)}` },
    { key: "transactionCount", label: "Transactions", width: "1fr", align: "right" },
    { key: "date", label: "Date", width: "1.2fr", align: "right" },
  ];
  const [marketInsights, setMarketInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("total_spend");
  const [sortDirection, setSortDirection] = useState("desc");

  // Filter states
  const [period, setPeriod] = useState("last_7_days");
  const [transactionStatus, setTransactionStatus] = useState("all");
  const [partnerStatus, setPartnerStatus] = useState("all");
  const abortControllerRef = useRef(null);

  const buildQueryParams = useCallback((pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("period", period);
    params.append("page", pageNum);
    params.append("page_size", 20);
    params.append("sort_by", sortBy);
    params.append("sort_direction", sortDirection);

    if (debouncedSearch.trim()) {
      params.append("search", debouncedSearch.trim());
    }
    if (transactionStatus && transactionStatus !== "all") {
      params.append("transaction_status", transactionStatus);
    }
    if (partnerStatus && partnerStatus !== "all") {
      params.append("partner_status", partnerStatus);
    }

    return params.toString();
  }, [period, sortBy, sortDirection, debouncedSearch, transactionStatus, partnerStatus]);

  const transformMarketInsightsData = useCallback((data) => {
    if (!data) return [];

    return data.map(item => ({
      id: item.id || item.customer_id || Math.random(),
      merchantId: item.merchant_id || item.vendor_listing_id || item.restaurant_id || "1",
      merchantName: item.merchant_name || item.restaurant_name || item.name || "Unknown",
      customerName: item.customer_name || item.name || "Unknown",
      totalSpend: item.total_spend || item.spend || item.amount || 0,
      transactionCount: item.transaction_count || item.transactions || item.count || 0,
      date: item.date || item.created_at || item.transaction_date || "Unknown",
    }));
  }, []);

  const fetchMarketInsights = useCallback(async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      const response = await get(`${endpoints.restaurantMarketInsightsShareOfWallet}?${buildQueryParams(pageNum)}`, { 
        signal: abortControllerRef.current.signal 
      });

      if (response.data?.status || response.data?.success) {
        const data = response.data.data || {};
        const rawData = data.data || data.insights || data.records || data || [];
        const newInsights = transformMarketInsightsData(rawData);

        setMarketInsights((prev) => {
          if (!isLoadMore || pageNum === 1) {
            return newInsights;
          }
          const existingIds = new Set(prev.map(item => item.id));
          const filteredNewInsights = newInsights.filter(item => !existingIds.has(item.id));
          return [...prev, ...filteredNewInsights];
        });

        const currentPage = data.current_page || pageNum;
        const lastPage = data.last_page;
        const hasMorePages = lastPage !== undefined ? currentPage < lastPage : newInsights.length === 20;
        setHasMore(hasMorePages);
        setPage(currentPage);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch customer insights:", error);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      abortControllerRef.current = null;
    }
  }, [buildQueryParams, transformMarketInsightsData]);

  const resetPagination = useCallback(() => {
    setPage(1);
    setHasMore(true);
    setMarketInsights([]);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchMarketInsights(page + 1, true);
    }
  }, [loadingMore, hasMore, page, fetchMarketInsights]);

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
  }, []);

  const handlePeriodChange = useCallback((val) => {
    setPeriod(val);
    resetPagination();
  }, [resetPagination]);

  const handleTransactionStatusChange = useCallback((val) => {
    setTransactionStatus(val.length === 0 ? "all" : val[0]);
    resetPagination();
  }, [resetPagination]);

  const handlePartnerStatusChange = useCallback((val) => {
    setPartnerStatus(val.length === 0 ? "all" : val[0]);
    resetPagination();
  }, [resetPagination]);

  const handleSortByChange = useCallback((val) => {
    setSortBy(val);
    resetPagination();
  }, [resetPagination]);

  const handleSortDirectionChange = useCallback((val) => {
    setSortDirection(val);
    resetPagination();
  }, [resetPagination]);

  const filters = useMemo(() => [
    {
      key: "period",
      type: "select",
      label: "Period",
      value: period,
      onChange: handlePeriodChange,
      defaultValue: "last_7_days",
      options: [
        { value: "last_7_days", label: "Last 7 Days" },
        { value: "last_30_days", label: "Last 30 Days" },
        { value: "this_month", label: "This Month" },
        { value: "year_to_date", label: "Year to Date" },
        { value: "all_time", label: "All Time" },
      ],
    },
    {
      key: "transactionStatus",
      type: "select",
      label: "Transaction Status",
      value: transactionStatus === "all" ? [] : [transactionStatus],
      onChange: handleTransactionStatusChange,
      options: [
        { value: "all", label: "All" },
        { value: "matched", label: "Matched" },
        { value: "unmatched", label: "Unmatched" },
      ],
    },
    {
      key: "partnerStatus",
      type: "select",
      label: "Partner Status",
      value: partnerStatus === "all" ? [] : [partnerStatus],
      onChange: handlePartnerStatusChange,
      options: [
        { value: "all", label: "All" },
        { value: "partner", label: "Partner" },
        { value: "non_partner", label: "Non Partner" },
      ],
    },
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: handleSortByChange,
      defaultValue: "total_spend",
      options: [
        { value: "total_spend", label: "Total Spend" },
        { value: "partner_spend", label: "Partner Spend" },
        { value: "non_partner_spend", label: "Non Partner Spend" },
        { value: "share_of_wallet", label: "Share of Wallet" },
        { value: "transactions_count", label: "Transactions Count" },
        { value: "latest_transaction", label: "Latest Transaction" },
        { value: "customer_name", label: "Customer Name" },
        { value: "created_at", label: "Created At" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: handleSortDirectionChange,
      defaultValue: "desc",
    },
  ], [period, transactionStatus, partnerStatus, sortBy, sortDirection, handlePeriodChange, handleTransactionStatusChange, handlePartnerStatusChange, handleSortByChange, handleSortDirectionChange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      resetPagination();
    }, 500);

    return () => clearTimeout(timer);
  }, [search, resetPagination]);

  useEffect(() => {
    fetchMarketInsights(1, false);
  }, [debouncedSearch, sortBy, sortDirection, period, transactionStatus, partnerStatus, fetchMarketInsights]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div>
            <h2 className="rp-card__title" style={{ margin: 0 }}>Customer Insights</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of customer spending patterns and transactions.</p>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search"
            filters={filters}
          />

        <DataTable
          columns={columns}
          rows={marketInsights}
          getRowKey={(r) => r.id}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        />
        </div>
      </Container>
    </div>
  );
};

AdminMarketInsightPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminMarketInsightPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminMarketInsightPage;
